import { THEMES } from '@shared/config/themes';

export interface FileByIdParams {
  fileId: string;
  content?: boolean;
}

export interface FileByEntityParams<E extends FileEntity> {
  entity: E;
  role?: FileRoleForEntity<E>;
  branding?: THEMES;
  content?: boolean;
}

export type FileParams<E extends FileEntity> =
  FileByIdParams | FileByEntityParams<E>;

export type FileByIdKey = keyof Pick<FileByIdParams, 'fileId'>;

export enum FileEntity {
  BRANDING = 'branding',
  LANDING_ITEM = 'landing-item',
  TILE_CONTENT = 'tile-content',
}

export const FILE_ENTITIES = new Set<FileEntity>(Object.values(FileEntity));

export const FILE_PUBLIC_ENTITIES = new Set<FileEntity>([FileEntity.BRANDING]);

export enum FileKind {
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
}

export enum FileStatus {
  ACTIVE = 'active',
  PENDING = 'pending',
  PROCESSING = 'processing',
  FAILED = 'failed',
}

export enum BrandingFileRole {
  BACKGROUND = 'background',
  HEADER = 'header',
  LANDING = 'landing',
  LOADING = 'loading',
  ICON = 'icon',
  CHATBOT = 'chatbot',
  CHATBOT_INVERSE = 'chatbot_inverse',
}

export enum LandingItemFileRole {
  DEFAULT = 'default',
  NOT_ALLOWED = 'not_allowed',
}

export const FILE_ENTITY_ROLES: Record<FileEntity, Set<string>> = {
  [FileEntity.BRANDING]: new Set(Object.values(BrandingFileRole)),
  [FileEntity.LANDING_ITEM]: new Set(Object.values(LandingItemFileRole)),
  [FileEntity.TILE_CONTENT]: new Set<string>(),
} as const;

export type FileEntityRoleMap = {
  [FileEntity.BRANDING]: BrandingFileRole;
  [FileEntity.LANDING_ITEM]: LandingItemFileRole;
  [FileEntity.TILE_CONTENT]: string;
};

export type FileRoleForEntity<E extends FileEntity> = FileEntityRoleMap[E];

export const FILE_ENTITIES_WITH_REQUIRED_ROLE = new Set<FileEntity>([
  FileEntity.BRANDING,
  FileEntity.LANDING_ITEM,
]);

export interface FileEntityDto<E extends FileEntity> {
  fileId: string;
  kind: FileKind;
  status: FileStatus;
  role: FileRoleForEntity<E>;
  theme: THEMES;
}

export enum ObjectFit {
  CONTAIN = 'contain',
  COVER = 'cover',
  FILL = 'fill',
  NONE = 'none',
}

export interface TileContentFileDto extends FileEntityDto<FileEntity.TILE_CONTENT> {
  withPadding: boolean | null;
  objectFit: ObjectFit | null;
}
